import React from 'react';
import ReactDOM from 'react-dom';
import get from 'lodash/get';
import { Provider } from 'react-redux';
import Amplify from '@aws-amplify/core';
import Auth from '@aws-amplify/auth';
import { withAuthenticator } from '@aws-amplify/ui-react';
import { ConnectedRouter } from 'connected-react-router/immutable';
import * as Sentry from '@sentry/react';
import config from '$config';
import { isMotrio, isRenault } from '$app/brand';
import history from '$app/utils/history';
import { normalizeCognitoAttributeName } from '$app/shared/utils/cognitoAttributeName';

import configureStore from './configureStore';
import './style/app.scss';

/**
 * Install Amplify Auth
 */

import '@aws-amplify/ui-react/styles.css';
import './styles/amplify-custom.css';

import MaiaTranslationProvider from '$app/i18n/MaiaTranslationProvider';
import { useMaiaT } from '$app/i18n/useMaiaT';
import AppShell from '$app/containers/AppShell';

// Initialize Sentry
Sentry.init({
  dsn: process.env.SENTRY_DSN || 'https://ca5606709c89494f9acb3ccf9c0ec4f3@o98558.ingest.us.sentry.io/501103',
  environment: config.env || 'development',
  release: process.env.SENTRY_RELEASE,
  debug: process.env.SENTRY_DEBUG === 'true',

  // Performance Monitoring - Reduced for noisy project
  tracesSampleRate: config.env === 'production' ? 0.01 : 0.1, // 1% prod, 10% dev

  // Session Replay - Only on errors
  replaysSessionSampleRate: 0, // No session replay on successful sessions
  replaysOnErrorSampleRate: config.env === 'production' ? 0.5 : 1.0, // 50% prod, 100% dev

  integrations: [
    Sentry.browserSessionIntegration(),
    Sentry.browserTracingIntegration({
      // Track navigation with react-router
      enableLongTask: true,
    }),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
      maskAllInputs: true,
    }),
    // Capture console errors
    Sentry.captureConsoleIntegration({ levels: ['error'] }),
  ],

  beforeSend(event, hint) {
    /**
     * Filter out unauthorized errors to avoid quota issues.
     * Marketing users accessing bookings without proper permissions
     * were causing tens of thousands of 403 errors via sockets.
     */
    const unauthorizedMessage = 'Request failed with status code 403';
    const eventExceptionsList = get(event, 'exception.values', []);
    const eventErrorMessage = eventExceptionsList.find((err) => err.value === unauthorizedMessage);
    const hintErrorMessage = get(hint, 'originalException.message');

    if (hintErrorMessage === unauthorizedMessage || eventErrorMessage === unauthorizedMessage) {
      return null;
    }
    return event;
  },
});

// Guard against Amplify/UI sending an empty attribute name in verification flow.
if (typeof Auth.verifyCurrentUserAttribute === 'function') {
  const verifyCurrentUserAttribute = Auth.verifyCurrentUserAttribute.bind(Auth);
  Auth.verifyCurrentUserAttribute = (attributeName, ...rest) =>
    verifyCurrentUserAttribute(normalizeCognitoAttributeName(attributeName), ...rest);
}

if (typeof Auth.verifyCurrentUserAttributeSubmit === 'function') {
  const verifyCurrentUserAttributeSubmit = Auth.verifyCurrentUserAttributeSubmit.bind(Auth);
  Auth.verifyCurrentUserAttributeSubmit = (attributeName, ...rest) =>
    verifyCurrentUserAttributeSubmit(normalizeCognitoAttributeName(attributeName), ...rest);
}

Amplify.configure({
  Auth: {
    region: config.cognito.region,
    userPoolId: config.cognito.userPoolId,
    userPoolWebClientId: config.cognito.clientId,
    mandatorySignIn: true,
  },
});

const MOUNT_NODE = document.getElementById('root');

// Create redux store with history
const initialState = {};
const store = configureStore(initialState, history);

function TranslatedErrorFallback() {
  const t = useMaiaT();
  return <h1>{t('maia.error.generic', 'Something went wrong.')}</h1>;
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error(error, errorInfo);
    Sentry.captureException(error);
  }

  render() {
    if (this.state.hasError) {
      return <TranslatedErrorFallback />;
    }

    return this.props.children;
  }
}

const App = withAuthenticator(AppShell, {
  hideSignUp: true,
  loginMechanisms: ['username'],
});

// Add brand attribute and title to authenticator
if (typeof document !== 'undefined') {
  const observer = new MutationObserver(() => {
    const authenticator = document.querySelector('[data-amplify-authenticator]');
    if (authenticator) {
      // Add brand attribute for CSS theming
      if (isRenault && !authenticator.hasAttribute('data-renault')) {
        authenticator.setAttribute('data-renault', 'true');
      }
      if (isMotrio && !authenticator.hasAttribute('data-motrio')) {
        authenticator.setAttribute('data-motrio', 'true');
      }

      // Add title if not already added
      if (!authenticator.querySelector('.auth-app-title')) {
        const router = authenticator.querySelector('[data-amplify-router]');
        if (router) {
          const title = document.createElement('h1');
          title.className = 'auth-app-title';
          if (isRenault) {
            title.textContent = 'RENAULT';
          } else if (isMotrio) {
            title.innerHTML = 'MOTRIO<br/>COUNTRY HUB';
          } else {
            title.textContent = 'MAIA';
          }
          router.insertBefore(title, router.firstChild);
        }
      }
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });
}

ReactDOM.render(
  <Provider store={store}>
    <MaiaTranslationProvider>
      <ConnectedRouter history={history}>
        <ErrorBoundary>
          <App />
        </ErrorBoundary>
      </ConnectedRouter>
    </MaiaTranslationProvider>
  </Provider>,
  MOUNT_NODE
);
